/**
 * Skills coverage: which puja × city pairs have nobody to send.
 *
 * This is the recruiting list. An empty cell is not a gap in a report — it is a puja the app hides
 * in that city, which is the honest behaviour: offering a booking nobody can serve is how a
 * marketplace breaks its central promise. A thin cell (under three) is a single point of failure:
 * one illness and the booking fails.
 *
 * **The count is the matcher's own answer** (E36). The server builds it from the same predicates
 * `matching` applies — active, listed, a *confirmed* skill for the puja, a live service area in the
 * city, and both mandatory badges — so a cell can never claim supply that dispatch would refuse.
 *
 * ⛔ **It is not availability, and the caption says so in the server's words.** Five of the
 * matcher's filters need a concrete booking — an open window, the blackout date, samagri, the
 * pilgrimage pause, the travel radius — and a puja × city pair has neither a clock nor an address.
 * The wording arrives in `basis` rather than being written here, so it cannot drift from what the
 * number actually means.
 *
 * Rendered as a plain table rather than a heat-map component — the design system ships no chart or
 * matrix family, and colour here is backed by the number in the cell, never carrying meaning alone.
 */
import { Button, Icon } from '../components/primitives';
import { Section } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { cellIndex, useCoverageGrid } from '../lib/api/hooks/supply';
import { UnservedDemand } from './UnservedDemand';

const HEAD: React.CSSProperties = {
  padding: '6px 10px',
  color: 'var(--c-ink3)',
  fontSize: 'var(--fs-admin-colhead)',
  fontWeight: 'var(--fw-regular)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-kicker)',
};

/** Zero is always red; under `thin_below` (the `supply.coverage_warn_depth` setting) amber. */
function cellSkin(n: number, thinBelow: number): React.CSSProperties {
  if (n === 0) return { background: 'var(--c-error-bg)', color: 'var(--c-error-text)' };
  if (n < thinBelow) return { background: 'var(--c-warn-bg)', color: 'var(--c-warn-text)' };
  return { background: 'transparent', color: 'var(--c-ink)' };
}

export function SkillsCoverage() {
  const { go } = useConsole();
  const grid = useCoverageGrid();

  if (grid.isPending) {
    return <LoadingState rows={6} message="Counting dispatchable pandits" />;
  }
  if (grid.isError) {
    return (
      <ErrorState title="Could not read the coverage grid" cause={errorMessage(grid.error)} />
    );
  }

  const { pujas, cities, cells, basis, thin_below: thinBelow } = grid.data;

  /* ⛔ No launched city, or no active puja, is a real state and not an error — a fresh
     environment has both. It is distinct from "the read failed", which is above, and from
     "every cell is zero", which is a finding the table itself shows. */
  if (pujas.length === 0 || cities.length === 0) {
    return (
      <EmptyState
        icon="grid-3x3"
        title="Nothing to plot yet"
        body="Coverage needs at least one launched city and one active puja."
        action={
          <Button size="sm" variant="secondary" icon="map-pin" onClick={() => go('geo')}>
            Open geography
          </Button>
        }
      />
    );
  }

  const counts = cellIndex(grid.data);
  const empty = cells.filter((cell) => cell.pandit_count === 0).length;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-warn-bg)',
          color: 'var(--c-warn-text)',
          border: '1px solid var(--c-warn)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
        }}
      >
        <Icon name="triangle-alert" size={16} />
        <span style={{ flex: 1 }}>
          {empty} puja × city cells have no dispatchable pandit. Those combinations are hidden in the
          app and are the recruiting list for supply.
        </span>
        <Button size="sm" variant="secondary" icon="users" onClick={() => go('pandits')}>
          Open directory
        </Button>
      </div>

      <UnservedDemand grid={grid.data} />

      <Section
        title="Skills coverage"
        note="Dispatchable pandits per puja per city — a thin cell is a booking that will not match"
        pad={false}
      >
        <div style={{ padding: 'var(--sp-lg)', overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--fs-admin-cell)' }}>
            <thead>
              <tr>
                <th style={{ ...HEAD, textAlign: 'left' }}>Puja</th>
                {cities.map((city) => (
                  <th key={city.id} style={{ ...HEAD, textAlign: 'right' }}>
                    {city.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pujas.map((puja) => (
                <tr key={puja.id}>
                  <td style={{ padding: '7px 10px', borderTop: '1px solid var(--c-border)', color: 'var(--c-ink)' }}>
                    {puja.name}
                  </td>
                  {cities.map((city) => {
                    const n = counts.get(`${puja.id}:${city.id}`) ?? 0;
                    return (
                      <td
                        key={city.id}
                        className="tabular"
                        aria-label={`${puja.name} in ${city.name}`}
                        style={{
                          padding: '7px 10px',
                          borderTop: '1px solid var(--c-border)',
                          textAlign: 'right',
                          ...cellSkin(n, thinBelow),
                        }}
                      >
                        {n}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <div style={{ display: 'flex', gap: 'var(--sp-lg)', fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--c-error-bg)', border: '1px solid var(--c-error)' }} />
          no pandit — hidden in the app
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--c-warn-bg)', border: '1px solid var(--c-warn)' }} />
          under {thinBelow} — single point of failure
        </span>
      </div>

      {/* ⛔ The server's wording, verbatim. What this number does NOT mean matters as much as
          what it does, and a caption written here would drift from the query the moment either
          moved. */}
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)', maxWidth: 760 }}>
        {basis}
      </p>
    </>
  );
}
